/**
 * <sk-switcher> — Card switcher with top tabs (sk-product-showcase).
 *
 * Markup (server-rendered):
 *   tabs:   [data-sk-switcher-tab] buttons (role="tab", aria-controls -> panel id)
 *   panels: direct children of [data-sk-switcher-panels] (one product card each)
 *
 * Progressive enhancement: without JS all cards are visible, stacked.
 */
class SkSwitcher extends HTMLElement {
  constructor() {
    super();
    this.tabs = Array.from(this.querySelectorAll('[data-sk-switcher-tab]'));
    const panelsEl = this.querySelector('[data-sk-switcher-panels]');
    this.panels = panelsEl ? Array.from(panelsEl.children) : [];
    if (!this.tabs.length || this.tabs.length !== this.panels.length) return;

    this.panels.forEach((panel, i) => {
      panel.setAttribute('role', 'tabpanel');
      panel.id = this.tabs[i].getAttribute('aria-controls');
      panel.setAttribute('aria-labelledby', this.tabs[i].id);
    });

    this.tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => this.select(i));
      tab.addEventListener('keydown', (event) => this.onKeydown(event, i));
    });

    const initial = Number.parseInt(this.dataset.defaultIndex ?? '0', 10);
    this.select(Number.isNaN(initial) ? 0 : Math.min(Math.max(initial, 0), this.tabs.length - 1));
  }

  select(index) {
    this.tabs.forEach((tab, i) => {
      const active = i === index;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
    });
    this.panels.forEach((panel, i) => {
      panel.hidden = i !== index;
    });
    this._active = index;
  }

  onKeydown(event, index) {
    const last = this.tabs.length - 1;
    let target = null;
    switch (event.key) {
      case 'ArrowRight':
        target = index === last ? 0 : index + 1;
        break;
      case 'ArrowLeft':
        target = index === 0 ? last : index - 1;
        break;
      case 'Home':
        target = 0;
        break;
      case 'End':
        target = last;
        break;
      default:
        return;
    }
    event.preventDefault();
    this.select(target);
    this.tabs[target].focus();
  }
}

customElements.define('sk-switcher', SkSwitcher);
